import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  EditorState,
  VideoTemplateEditorContextProvider,
  VideoTemplateState,
} from 'components/VideoTemplateEditor';
import PreviewPane from 'components/VideoTemplateEditor/PreviewPane';
import { PodcastWorkflowTemplate } from 'redux/middleware/api/podcast-service/types';
import { block } from './utils';

export interface TemplatePreviewProps {
  className?: string;
  template: PodcastWorkflowTemplate;
}

/**
 * Renders the template preview for the landing page.
 * - When template has previewVideos: autoplays the preview video on mute (like hover on template tiles)
 * - When template has no previewVideos: renders the config-based preview via PreviewPane
 *
 * Aspect ratio: Both paths use a flexible container. The config path uses ResizeDetector +
 * getCanvasDimensions to fit the canvas. The video path uses object-fit: contain so the
 * video maintains its intrinsic aspect ratio within the same container.
 */
const TemplatePreview: React.FC<TemplatePreviewProps> = ({
  className,
  template,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hasPreviewVideos = template?.previewVideos?.length > 0;

  const [editorState, setEditorState] = useState<VideoTemplateState>(() =>
    EditorState.createStateFromTemplate(template),
  );

  const handleChange = useCallback(
    (stateUpdater: (prev: VideoTemplateState) => VideoTemplateState) => {
      setEditorState(prevState => stateUpdater(prevState));
    },
    [],
  );

  useEffect(() => {
    if (hasPreviewVideos && videoRef.current) {
      videoRef.current.play().catch(() => {
        // Autoplay may be blocked; muted videos typically work in modern browsers
      });
    }
  }, [hasPreviewVideos]);

  if (hasPreviewVideos) {
    return (
      <div className={block('preview', { videoBased: true })}>
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          className={block('preview-video')}
          poster={template.thumbnailImageUrl}
        >
          {template.previewVideos.map(({ videoUrl, mediaType }) => (
            <source key={videoUrl} src={videoUrl} type={mediaType} />
          ))}
        </video>
      </div>
    );
  }

  return (
    <VideoTemplateEditorContextProvider
      disabled
      onChange={handleChange}
      state={editorState}
    >
      <PreviewPane className={className} />
    </VideoTemplateEditorContextProvider>
  );
};

export default TemplatePreview;
